import { useMemo } from 'react';
import {
  ArrowLeft,
  FileText,
  ExternalLink,
  Eye,
  Shield,
  Gauge,
  Link2Off,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import type { PageResult, AccessibilityIssue, EaaIssue, BrokenLink } from '../../types';
import { GradeBadge, getGradeFromScore } from '../ui/GradeBadge';
import { SeverityDot } from '../ui/SeverityBadge';
import { IssueCard } from '../ui/IssueCard';
import type { IssueCardData, WcagPrinciple, ToolSource } from '../ui/IssueCard';

interface PageDetailViewProps {
  page: PageResult;
  onBack: () => void;
}

function getPrincipleFromWcag(wcagCriteria: string[]): WcagPrinciple | undefined {
  if (!wcagCriteria || wcagCriteria.length === 0) return undefined;
  const firstCriterion = wcagCriteria[0];
  const principleNumber = firstCriterion.split('.')[0];
  switch (principleNumber) {
    case '1': return 'perceivable';
    case '2': return 'operable';
    case '3': return 'understandable';
    case '4': return 'robust';
    default: return undefined;
  }
}

function convertAccessibilityIssue(issue: AccessibilityIssue): IssueCardData {
  return {
    ruleId: issue.ruleId,
    title: issue.description.split('.')[0] || issue.ruleId,
    description: issue.description,
    severity: issue.impact,
    wcagCriteria: issue.wcagCriteria,
    wcagLevel: issue.wcagLevel,
    wcagPrinciple: getPrincipleFromWcag(issue.wcagCriteria),
    toolSource: issue.toolSource as ToolSource,
    helpUrl: issue.helpUrl,
    affectedElements: issue.nodes?.map(node => ({
      selector: node.target?.join(' > ') || '',
      html: node.html,
      failureSummary: node.failureSummary,
    })),
    count: issue.nodes?.length || 1,
  };
}

function convertEaaIssue(issue: EaaIssue): IssueCardData {
  return {
    ruleId: issue.clauseNumber,
    title: issue.requirement,
    description: issue.description,
    severity: issue.severity,
    wcagCriteria: issue.wcagMapping,
    remediation: issue.remediation,
    toolSource: issue.toolSource as ToolSource,
    count: 1,
  };
}

interface SectionHeaderProps {
  icon: typeof Eye;
  title: string;
  count: number;
  color?: string;
}

function SectionHeader({ icon: Icon, title, count, color = 'text-coral' }: SectionHeaderProps) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-heading-3 font-semibold flex items-center gap-2">
        <Icon className={color} size={24} />
        {title}
      </h3>
      <span className="text-body-sm text-gray-medium">{count} issues</span>
    </div>
  );
}

interface IssueCountBadgeProps {
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
}

function IssueCountBadge({ critical, serious, moderate, minor }: IssueCountBadgeProps) {
  return (
    <div className="flex items-center gap-3 text-body-sm">
      {critical > 0 && (
        <span className="flex items-center gap-1">
          <SeverityDot severity="critical" size="sm" />
          {critical}
        </span>
      )}
      {serious > 0 && (
        <span className="flex items-center gap-1">
          <SeverityDot severity="serious" size="sm" />
          {serious}
        </span>
      )}
      {moderate > 0 && (
        <span className="flex items-center gap-1">
          <SeverityDot severity="moderate" size="sm" />
          {moderate}
        </span>
      )}
      {minor > 0 && (
        <span className="flex items-center gap-1">
          <SeverityDot severity="minor" size="sm" />
          {minor}
        </span>
      )}
      {critical === 0 && serious === 0 && moderate === 0 && minor === 0 && (
        <span className="flex items-center gap-1 text-status-success">
          <CheckCircle size={14} />
          No issues
        </span>
      )}
    </div>
  );
}

function BrokenLinkItem({ link }: { link: BrokenLink }) {
  return (
    <div className="border border-gray-light rounded-lg p-4 bg-white">
      <div className="flex items-start gap-3">
        <Link2Off size={18} className="text-status-error flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 rounded text-caption font-medium bg-red-100 text-red-700">
              {link.statusCode || 'Error'}
            </span>
            {link.isExternal && (
              <span className="px-2 py-0.5 rounded text-caption bg-blue-100 text-blue-700">
                External
              </span>
            )}
          </div>
          <a
            href={link.targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body text-coral hover:underline block truncate"
          >
            {link.targetUrl}
          </a>
          {link.linkText && (
            <p className="text-body-sm text-gray-medium mt-1">
              Link text: "{link.linkText}"
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function PageDetailView({ page, onBack }: PageDetailViewProps) {
  const grade = getGradeFromScore(page.score);

  // Count issues by category
  const accessibilityIssues = page.accessibilityIssues || [];
  const eaaIssues = page.eaaIssues || [];
  // Links the scanner could not verify are a human check, not confirmed breaks —
  // they live in the Broken Links tab's own section, not in this page's count.
  const brokenLinks = (page.brokenLinks || []).filter((link) => !link.needsManualReview);

  // Calculate severity counts for accessibility issues
  const accessibilityCounts = useMemo(() => {
    return accessibilityIssues.reduce(
      (acc, issue) => {
        acc[issue.impact]++;
        return acc;
      },
      { critical: 0, serious: 0, moderate: 0, minor: 0 }
    );
  }, [accessibilityIssues]);

  // Calculate severity counts for EAA issues
  const eaaCounts = useMemo(() => {
    return eaaIssues.reduce(
      (acc, issue) => {
        acc[issue.severity]++;
        return acc;
      },
      { critical: 0, serious: 0, moderate: 0, minor: 0 }
    );
  }, [eaaIssues]);

  // Convert and group accessibility issues by ruleId (same rule, multiple elements → one card)
  const accessibilityCardData = useMemo(() => {
    const grouped = new Map<string, IssueCardData>();
    for (const issue of accessibilityIssues) {
      const existing = grouped.get(issue.ruleId);
      const converted = convertAccessibilityIssue(issue);
      if (existing) {
        existing.affectedElements = [
          ...(existing.affectedElements || []),
          ...(converted.affectedElements || []),
        ];
        existing.count = (existing.count || 0) + (converted.count || 1);
      } else {
        grouped.set(issue.ruleId, converted);
      }
    }
    return Array.from(grouped.values());
  }, [accessibilityIssues]);

  // Convert and group EAA issues by clauseNumber (dedup same clause on same page)
  const eaaCardData = useMemo(() => {
    const grouped = new Map<string, IssueCardData>();
    for (const issue of eaaIssues) {
      if (!grouped.has(issue.clauseNumber)) {
        grouped.set(issue.clauseNumber, convertEaaIssue(issue));
      }
    }
    return Array.from(grouped.values());
  }, [eaaIssues]);

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-body text-gray-medium hover:text-coral transition-colors"
        >
          <ArrowLeft size={20} />
          Back to summary
        </button>
      </div>

      {/* Page Info Card */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          {/* Page URL and Title */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={20} className="text-coral flex-shrink-0" />
              <h2 className="text-heading-2 font-semibold truncate">
                Page Details
              </h2>
            </div>
            <a
              href={page.pageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-body text-coral hover:underline group"
            >
              <span className="truncate">{page.pageUrl}</span>
              <ExternalLink size={14} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>

          {/* Score Badge */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="flex items-center gap-3">
                <GradeBadge grade={grade} size="lg" />
                <div>
                  <div className="text-heading-2 font-bold">{page.score}</div>
                  <div className="text-caption text-gray-medium">/ 100</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-6 pt-6 border-t border-gray-light">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-medium mb-1">
                <Eye size={16} />
                <span className="text-body-sm font-medium">Accessibility</span>
              </div>
              <IssueCountBadge {...accessibilityCounts} />
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-medium mb-1">
                <Shield size={16} />
                <span className="text-body-sm font-medium">EAA Compliance</span>
              </div>
              <IssueCountBadge {...eaaCounts} />
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-medium mb-1">
                <Link2Off size={16} />
                <span className="text-body-sm font-medium">Broken Links</span>
              </div>
              <span className="text-body-sm">
                {brokenLinks.length > 0 ? (
                  <span className="text-status-error font-medium">{brokenLinks.length} found</span>
                ) : (
                  <span className="flex items-center gap-1 text-status-success">
                    <CheckCircle size={14} />
                    None
                  </span>
                )}
              </span>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-medium mb-1">
                <AlertTriangle size={16} />
                <span className="text-body-sm font-medium">Total Issues</span>
              </div>
              <span className="text-body font-bold">{page.issueCount}</span>
            </div>
          </div>
        </div>

        {/* Lighthouse Scores if available */}
        {page.lighthouseScores && (
          <div className="mt-6 pt-6 border-t border-gray-light">
            <h4 className="text-body font-semibold text-gray-medium mb-3 flex items-center gap-2">
              <Gauge size={16} />
              Lighthouse Scores
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Performance', score: page.lighthouseScores.performance },
                { label: 'Accessibility', score: page.lighthouseScores.accessibility },
                { label: 'Best Practices', score: page.lighthouseScores.bestPractices },
                { label: 'SEO', score: page.lighthouseScores.seo },
              ]
                // performance may be absent (only present from PageSpeed Insights) —
                // skip any score we don't have rather than render "undefined".
                .filter((item): item is { label: string; score: number } =>
                  item.score !== undefined && item.score !== null
                )
                .map(({ label, score }) => (
                <div key={label} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-body-sm text-gray-medium">{label}</span>
                  <span className={`text-body font-bold ${
                    score >= 90 ? 'text-status-success' :
                    score >= 50 ? 'text-status-warning' : 'text-status-error'
                  }`}>
                    {score}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Accessibility Issues */}
      {accessibilityIssues.length > 0 && (
        <div>
          <SectionHeader
            icon={Eye}
            title="Accessibility Issues"
            count={accessibilityIssues.length}
          />
          <div className="space-y-4">
            {accessibilityCardData.map((issue) => (
              <IssueCard key={issue.ruleId} issue={issue} />
            ))}
          </div>
        </div>
      )}

      {/* EAA Compliance Issues */}
      {eaaIssues.length > 0 && (
        <div>
          <SectionHeader
            icon={Shield}
            title="EAA Compliance Issues"
            count={eaaIssues.length}
          />
          <div className="space-y-4">
            {eaaCardData.map((issue, idx) => (
              <IssueCard key={`${issue.ruleId}-${idx}`} issue={issue} />
            ))}
          </div>
        </div>
      )}

      {/* Broken Links */}
      {brokenLinks.length > 0 && (
        <div>
          <SectionHeader
            icon={Link2Off}
            title="Broken Links"
            count={brokenLinks.length}
            color="text-status-error"
          />
          <div className="space-y-3">
            {brokenLinks.map((link, idx) => (
              <BrokenLinkItem key={`${link.targetUrl}-${idx}`} link={link} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state if no issues */}
      {accessibilityIssues.length === 0 && eaaIssues.length === 0 && brokenLinks.length === 0 && (
        <div className="card text-center py-12">
          <CheckCircle size={48} className="mx-auto text-status-success opacity-50 mb-4" />
          <h3 className="text-heading-3 font-semibold text-dark-text mb-2">
            No Issues Found on This Page
          </h3>
          <p className="text-body text-gray-medium">
            This page passed all accessibility and compliance checks.
          </p>
        </div>
      )}
    </div>
  );
}
